export function installVue2Logger(Vue, options) {
    const previous = Vue.config.errorHandler;
    Vue.config.errorHandler = (err, vm, info) => {
        options.logger.error(err, { source: 'vue.config.errorHandler', info, component: componentName(vm) });
        if (previous)
            previous(err, vm, info);
    };
    Vue.prototype.$logger = options.logger;
}
export function instrumentVueRouter(router, options) {
    let currentRoute;
    let currentEnteredAt = Date.now();
    router.beforeEach?.((to, from, next) => {
        const fromMeta = routeMeta(from);
        const toMeta = routeMeta(to);
        options.logger.event('navigation.route_change_started', { from: fromMeta, to: toMeta, fromPath: fromMeta?.path, toPath: toMeta?.path, currentDwellMs: Date.now() - currentEnteredAt });
        next();
    });
    router.afterEach?.((to, from) => {
        const now = Date.now();
        const fromMeta = routeMeta(from);
        const toMeta = routeMeta(to);
        const dwellMs = currentRoute?.path === fromMeta?.path ? now - currentEnteredAt : undefined;
        options.logger.event('navigation.route_change_succeeded', { from: fromMeta, to: toMeta, fromPath: fromMeta?.path, toPath: toMeta?.path, previousDwellMs: dwellMs });
        options.logger.screen(screenName(to), { path: toMeta?.path, routeName: toMeta?.name, fromPath: fromMeta?.path, fromName: fromMeta?.name, dwellMs });
        currentRoute = toMeta;
        currentEnteredAt = now;
    });
    router.onError?.((error) => {
        options.logger.error(error, { source: 'vue-router' });
    });
}
export function logRouteGuardRejection(logger, details) {
    const to = routeMeta(details.to);
    const from = routeMeta(details.from);
    logger.auth('navigation.guard_rejected', {
        reason: details.reason,
        to,
        from,
        toPath: to?.path,
        toName: to?.name,
        fromPath: from?.path,
        fromName: from?.name,
    });
}
// Note: authProvider is accepted for backward compatibility but is no longer needed –
// the browser SDK's logger.auth() now auto-merges the registered authProvider snapshot.
export function instrumentAxios(axios, options) {
    axios.interceptors.request.use((config) => {
        config.metadata = { ...(config.metadata || {}), startedAt: Date.now(), clientSessionId: options.logger.getSessionId() };
        config.headers = { ...(config.headers || {}), 'X-Client-Session-Id': options.logger.getSessionId(), 'X-Client-Id': options.logger.getClientId() };
        return config;
    });
    axios.interceptors.response.use((response) => {
        const durationMs = Date.now() - (response.config.metadata?.startedAt || Date.now());
        const url = normalizeAxiosUrl(response.config.url);
        if (isIgnoredUrl(url, options.ignoredUrls))
            return response;
        if (durationMs > (options.slowRequestMs || 3000)) {
            options.logger.warn('http.request_slow', httpMeta(response.config, response.status, durationMs));
        }
        return response;
    }, (error) => {
        const config = error.config || {};
        const durationMs = Date.now() - (config.metadata?.startedAt || Date.now());
        const status = error.response?.status;
        const url = normalizeAxiosUrl(config.url);
        if (isIgnoredUrl(url, options.ignoredUrls))
            return Promise.reject(error);
        if (status === 401) {
            // For 401 events, use logger.auth() so the authProvider snapshot is auto-merged
            // into the top-level auth field. httpMeta is passed as explicit auth, which overrides
            // any conflicting snapshot fields (since snapshot merges first, explicit overrides second).
            options.logger.auth('http.auth_error', httpMeta(config, status, durationMs, error.code));
        }
        else {
            options.logger.warn('http.request_failed', httpMeta(config, status, durationMs, error.code));
        }
        return Promise.reject(error);
    });
}
function normalizeAxiosUrl(url) {
    if (!url)
        return undefined;
    try {
        // If relative, return without query string
        const u = new URL(url, typeof window !== 'undefined' ? window.location.href : 'http://localhost');
        return `${u.origin}${u.pathname}`;
    }
    catch {
        return url.split('?')[0];
    }
}
function isIgnoredUrl(url, ignoredUrls) {
    if (!url || !ignoredUrls || ignoredUrls.length === 0)
        return false;
    return ignoredUrls.some((rule) => typeof rule === 'string' ? url.includes(rule) : rule.test(url));
}
function routeMeta(route) {
    return route ? { name: route.name, path: route.path, queryKeys: Object.keys(route.query || {}), paramKeys: Object.keys(route.params || {}) } : undefined;
}
function screenName(route) {
    return String(route?.meta?.screenName || route?.meta?.title || route?.name || route?.path || 'unknown screen');
}
function httpMeta(config, status, durationMs, code) {
    return { method: config.method, url: normalizeAxiosUrl(config.url), status, durationMs, code };
}
function componentName(vm) {
    return vm?.$options?.name || vm?.$options?._componentTag;
}
